/**
 * DosyaTakip.jsx — the file tracking list under a document card of the stand detail's "Proje Bilgileri" tab
 * (backlog #211): per file - uploaded or not, the review status, the upload date, who reviewed it and when,
 * the rejection reason - and, per title, the title-level decision and the revisions still open.
 *
 * Presentational only: the page builds `entries` with the helpers of dosyaTakipYardimci.js and passes the staff names.
 *   entries   [{ key, label|null, file|null }]  label = a photo corner; file = null means "not uploaded"
 *   names     { userId: "Ad Soyad" }            reviewers (GET /users/staff); an unknown id shows as "#id"
 *   decision  { status, by, at } | null         the title-level decision (project.fileApprovals)
 *   openRevisions   unfinished DOCUMENT revisions of the title; the badge opens the newest through onOpenRevision
 *   onPreview(file) opens the file (DocumentPreviewModal on the page)
 */
import React from "react";
import { Badge } from "reactstrap";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { fmtTarih, personLabel } from "./dosyaTakipYardimci";

function statusInfo(status) {
  return FILE_APPROVAL_STATUS[status ?? 0] ?? FILE_APPROVAL_STATUS[0];
}

export default function DosyaTakip({
  entries, names, onPreview, decision, openRevisions, onOpenRevision, emptyText,
}) {
  const { t } = useTranslation("projeler");
  const list = entries || [];
  const revisions = openRevisions || [];
  const decided = decision && (decision.status !== 0 || decision.by != null);
  const decisionBy = decided ? personLabel(decision.by, names) : null;

  return (
    <div className="w-100 text-start mt-3 pt-2 border-top" data-testid="dosya-takip">
      {decided && (
        <div className="text-muted mb-1" style={{ fontSize: 11 }} data-testid="dosya-takip-karar">
          {t("dosyaTakip.baslikKarari", { durum: statusInfo(decision.status).label })}
          {decisionBy ? ` · ${decisionBy}` : ""}
          {decision.at ? ` · ${fmtTarih(decision.at)}` : ""}
        </div>
      )}

      {revisions.length > 0 && (
        <Badge
          color="danger"
          className="mb-1 d-inline-flex align-items-center gap-1"
          style={{ cursor: onOpenRevision ? "pointer" : undefined }}
          title={t("dosyaTakip.acikRevizyonuAc")}
          onClick={onOpenRevision ? () => onOpenRevision(revisions[0]) : undefined}
          data-testid="dosya-takip-revizyon"
        >
          <AlertTriangle size={11} />{t("dosyaTakip.acikRevizyon", { count: revisions.length })}
        </Badge>
      )}

      {list.length === 0 ? (
        <div className="text-muted fst-italic small" data-testid="dosya-takip-bos">{emptyText ?? t("dosyaTakip.dosyaYuklenmedi")}</div>
      ) : (
        list.map(({ key, label, file }) => {
          const info = file ? statusInfo(file.approvalStatus) : null;
          const reviewer = file ? personLabel(file.reviewedBy, names) : null;
          const title = label ?? file?.originalFileName ?? "";
          return (
            <div key={key} className="py-1 border-bottom small" data-testid="dosya-takip-satir">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="text-truncate fw-semibold" title={title}>{title}</span>
                {file ? (
                  <Badge color={info.color} className="fw-normal flex-shrink-0">{info.label}</Badge>
                ) : (
                  <Badge color="secondary" className="fw-normal flex-shrink-0">{t("dosyaTakip.yuklenmedi")}</Badge>
                )}
              </div>
              {file && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {label && (
                    onPreview ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0 align-baseline text-truncate"
                        style={{ fontSize: 11, maxWidth: "100%" }}
                        title={file.originalFileName}
                        onClick={() => onPreview(file)}
                      >
                        {file.originalFileName}
                      </button>
                    ) : file.originalFileName
                  )}
                  {label && " · "}
                  {t("dosyaTakip.yuklendi", { tarih: fmtTarih(file.uploadedAt) })}
                  {(file.reviewedAt || reviewer) && (
                    <>
                      {" · "}
                      {t("dosyaTakip.inceleyen", { ad: reviewer ?? "—", tarih: fmtTarih(file.reviewedAt) })}
                    </>
                  )}
                </div>
              )}
              {file && !label && onPreview && (
                <button
                  type="button"
                  className="btn btn-link p-0 border-0 align-baseline"
                  style={{ fontSize: 11 }}
                  onClick={() => onPreview(file)}
                >
                  {t("dosyaTakip.dosyayiAc")}
                </button>
              )}
              {file && Number(file.approvalStatus) === 2 && file.rejectReason && (
                <div className="text-danger" style={{ fontSize: 11 }}>
                  {t("dosyaTakip.redGerekcesi", { gerekce: file.rejectReason })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
